import React, { useState, useEffect } from 'react';
import { Eye, RotateCcw, Webhook, ChevronLeft, ChevronRight, Loader2, X } from 'lucide-react';
import { adminWebhookLogsAPI } from '../../../services/api';
import { toast } from 'react-toastify';
import ActionMenu from '../../shared/ActionMenu';

/* ── design tokens ── */
const C = {
  bg: '#f4f4f9', surface: '#FFFFFF', border: '#e8e8f1', borderSoft: '#f0f0f6',
  ink: '#14142b', body: '#4a4a6a', muted: '#6e6e86',
  primary: '#7c3aed', primarySoft: '#f1ebff', primaryTint: '#ddd6fe',
  success: '#10B981', successSoft: '#D1FAE5',
  warning: '#F59E0B', warnSoft: '#FEF3C7',
  danger: '#EF4444', dangerSoft: '#FEE2E2',
  radius: 14, radiusSm: 8,
};

const fmtDate = (iso) => iso ? new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

const StatusBadge = ({ status }) => {
  const map = {
    success:   { bg: C.successSoft, color: C.success,  label: 'Success'   },
    pending:   { bg: C.warnSoft,    color: C.warning,  label: 'Pending'   },
    failed:    { bg: C.dangerSoft,  color: C.danger,   label: 'Failed'    },
    exhausted: { bg: '#F1F5F9',     color: C.muted,    label: 'Exhausted' },
  };
  const s = map[status] || { bg: '#F1F5F9', color: C.muted, label: status };
  return <span style={{ padding: '3px 10px', borderRadius: 999, background: s.bg, color: s.color, fontSize: 11, fontWeight: 700, letterSpacing: .3 }}>{s.label}</span>;
};

/* ══════════════════════════════════════════════════ */
const AdminWebhookLogs = () => {
  const [list,    setList]    = useState({ data: [], last_page: 1 });
  const [page,    setPage]    = useState(1);
  const [status,  setStatus]  = useState('');
  const [txnId,   setTxnId]   = useState('');
  const [loading, setLoading] = useState(true);
  const [detail,  setDetail]  = useState(null);
  const [replaying, setReplaying] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = { page };
      if (status) params.status = status;
      if (txnId)  params.transaction_id = txnId;
      const { data } = await adminWebhookLogsAPI.list(params);
      setList(data?.data || { data: [], last_page: 1 });
    } catch { toast.error('Failed to load webhook logs'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [page, status]);

  const handleReplay = async (id) => {
    setReplaying(id);
    try { await adminWebhookLogsAPI.replay(id); toast.success('Replay queued'); load(); }
    catch { toast.error('Replay failed'); }
    finally { setReplaying(null); }
  };

  const showDetail = async (row) => {
    try { const { data } = await adminWebhookLogsAPI.show(row.id); setDetail(data?.data); }
    catch { toast.error('Failed to load detail'); }
  };

  const inputStyle = { height: 38, borderRadius: C.radiusSm, border: `1px solid ${C.border}`, padding: '0 12px', fontSize: 13, color: C.ink, background: C.surface, outline: 'none' };

  return (
    <div style={{ maxWidth: 1280, margin: '0 auto' }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>

      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: C.ink, margin: 0 }}>Webhook Logs</h1>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: C.muted }}>
          Outbound merchant webhooks. Failed deliveries auto-retry at 5 min, 15 min, 1 hr.
        </p>
      </div>

      {/* Filters */}
      <div style={{ background: C.surface, border: `1.5px solid ${C.border}`, borderRadius: C.radiusSm, padding: '10px 16px', marginBottom: 20, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        {[
          { label: 'All',       val: '',          color: C.primary },
          { label: 'Pending',   val: 'pending',   color: C.warning },
          { label: 'Success',   val: 'success',   color: C.success },
          { label: 'Failed',    val: 'failed',    color: C.danger  },
          { label: 'Exhausted', val: 'exhausted', color: C.muted   },
        ].map(t => (
          <button key={t.val} onClick={() => { setStatus(t.val); setPage(1); }}
            style={{ height: 34, padding: '0 14px', borderRadius: 999, border: `1.5px solid ${status === t.val ? t.color : C.border}`, background: status === t.val ? t.color : 'transparent', color: status === t.val ? '#fff' : C.body, fontWeight: 600, fontSize: 12, cursor: 'pointer', transition: 'all .15s', whiteSpace: 'nowrap', fontFamily: 'inherit' }}>
            {t.label}
          </button>
        ))}
        <input style={{ ...inputStyle, flex: 1, minWidth: 160 }} placeholder="Filter by Transaction ID…" value={txnId} onChange={e => setTxnId(e.target.value)} onKeyDown={e => e.key === 'Enter' && load()} />
        <button onClick={load} style={{ height: 38, padding: '0 16px', borderRadius: C.radiusSm, background: C.primary, color: '#fff', border: 'none', fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>Apply</button>
      </div>

      {/* Table */}
      <div style={{ background: C.surface, borderRadius: C.radius, border: `1px solid ${C.border}`, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 60, textAlign: 'center', color: C.muted }}>
            <Loader2 size={28} style={{ animation: 'spin .8s linear infinite' }} />
            <p style={{ marginTop: 12, fontSize: 13 }}>Loading…</p>
          </div>
        ) : (list.data || []).length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center', color: C.muted }}>
            <Webhook size={36} style={{ opacity: .3, marginBottom: 12 }} />
            <p style={{ fontSize: 13 }}>No webhook deliveries found.</p>
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                <thead>
                  <tr style={{ background: C.bg, borderBottom: `1px solid ${C.border}` }}>
                    {['Created', 'Transaction', 'URL', 'Attempt', 'Status', 'HTTP', 'Next Retry', 'Actions'].map(h => (
                      <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 700, fontSize: 11, color: C.muted, textTransform: 'uppercase', letterSpacing: .5, whiteSpace: 'nowrap' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {list.data.map((d, i) => (
                    <tr key={d.id} style={{ borderBottom: `1px solid ${C.borderSoft}`, background: i % 2 === 0 ? C.surface : C.bg }}>
                      <td style={{ padding: '12px 16px', fontSize: 12, color: C.muted, whiteSpace: 'nowrap' }}>{fmtDate(d.created_at)}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <code style={{ fontSize: 11, fontFamily: 'Menlo,Consolas,monospace', color: C.primary }}>{d.transaction_id}</code>
                      </td>
                      <td style={{ padding: '12px 16px', maxWidth: 260 }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: 12, color: C.body }} title={d.webhook_url}>{d.webhook_url}</div>
                      </td>
                      <td style={{ padding: '12px 16px', fontWeight: 700, color: C.ink, textAlign: 'center' }}>{d.attempt}</td>
                      <td style={{ padding: '12px 16px' }}><StatusBadge status={d.status} /></td>
                      <td style={{ padding: '12px 16px', fontSize: 12, color: d.http_code >= 200 && d.http_code < 300 ? C.success : C.danger, fontWeight: 700 }}>{d.http_code || '—'}</td>
                      <td style={{ padding: '12px 16px', fontSize: 12, color: C.muted }}>{d.next_attempt_at ? fmtDate(d.next_attempt_at) : '—'}</td>
                      <td style={{ padding: '12px 16px' }}>
                        <ActionMenu items={[
                          { label: 'View Detail', Icon: Eye, color: C.primary, bg: C.primarySoft, onClick: () => showDetail(d) },
                          { label: 'Replay', Icon: RotateCcw, color: C.warning, bg: C.warnSoft, loading: replaying === d.id, onClick: () => handleReplay(d.id), hidden: !(d.status === 'failed' || d.status === 'exhausted') },
                        ]} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {list.last_page > 1 && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', padding: '14px 20px', gap: 4, borderTop: `1px solid ${C.borderSoft}` }}>
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  style={{ width: 30, height: 30, borderRadius: 6, border: `1px solid ${C.border}`, background: C.surface, cursor: 'pointer', color: C.body, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ChevronLeft size={14} />
                </button>
                {Array.from({ length: list.last_page }, (_, i) => i + 1).map(p => (
                  <button key={p} onClick={() => setPage(p)} style={{
                    width: 30, height: 30, borderRadius: 6,
                    border: `1px solid ${p === page ? C.primary : C.border}`,
                    background: p === page ? C.primary : C.surface,
                    color: p === page ? '#fff' : C.body,
                    fontWeight: p === page ? 700 : 400, fontSize: 12, cursor: 'pointer',
                  }}>{p}</button>
                ))}
                <button onClick={() => setPage(p => Math.min(list.last_page, p + 1))} disabled={page === list.last_page}
                  style={{ width: 30, height: 30, borderRadius: 6, border: `1px solid ${C.border}`, background: C.surface, cursor: 'pointer', color: C.body, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ChevronRight size={14} />
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Detail Modal */}
      {detail && (
        <div onClick={() => setDetail(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(20,20,43,.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(3px)' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: C.surface, borderRadius: C.radius + 2, width: '100%', maxWidth: 640, maxHeight: '80vh', overflow: 'auto', padding: 28, boxShadow: '0 20px 60px rgba(20,20,43,.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ fontSize: 16, fontWeight: 800, color: C.ink, margin: 0 }}>Webhook Delivery #{detail.id}</h3>
              <button onClick={() => setDetail(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted }}>
                <X size={18} />
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
              {[
                { label: 'Transaction', val: detail.transaction_id, mono: true },
                { label: 'URL',         val: detail.webhook_url,    mono: true },
                { label: 'Attempt',     val: detail.attempt         },
                { label: 'HTTP Code',   val: detail.http_code || '—' },
              ].map(({ label, val, mono }) => (
                <div key={label}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: .4, marginBottom: 4 }}>{label}</div>
                  {mono
                    ? <code style={{ fontSize: 12, color: C.ink, wordBreak: 'break-all', fontFamily: 'Menlo,Consolas,monospace' }}>{val}</code>
                    : <div style={{ fontSize: 13, color: C.ink, fontWeight: 600 }}>{val}</div>
                  }
                </div>
              ))}
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: .4, marginBottom: 8 }}>Status</div>
              <StatusBadge status={detail.status} />
            </div>

            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.body, marginBottom: 6 }}>Payload</div>
              <pre style={{ background: '#14142b', color: '#e8e8f1', padding: 14, borderRadius: C.radiusSm, fontSize: 11, maxHeight: 200, overflow: 'auto', margin: 0, fontFamily: 'Menlo,Consolas,monospace', lineHeight: 1.6 }}>{detail.payload}</pre>
            </div>

            {detail.response_body && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.body, marginBottom: 6 }}>Response Body</div>
                <pre style={{ background: '#1E293B', color: '#FED7AA', padding: 14, borderRadius: C.radiusSm, fontSize: 11, maxHeight: 160, overflow: 'auto', margin: 0, fontFamily: 'Menlo,Consolas,monospace', lineHeight: 1.6 }}>{detail.response_body}</pre>
              </div>
            )}

            {detail.signature && (
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.body, marginBottom: 6 }}>Signature</div>
                <code style={{ fontSize: 11, color: C.muted, wordBreak: 'break-all', fontFamily: 'Menlo,Consolas,monospace' }}>{detail.signature}</code>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminWebhookLogs;
